import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import Logo from "../../components/Logo";
import {
  LogOut, Users, Stethoscope, HeartHandshake,
  GraduationCap, Newspaper, BookOpen, Mail, ArrowRight,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, accent = "blue" }) {
  const accents = {
    blue: "bg-[#F0F9FF] text-[#0369A1]",
    teal: "bg-[#ECFDF5] text-[#0F766E]",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${accents[accent]}`}><Icon size={20} /></span>
        <div>
          <div className="text-[12.5px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
          <div className="text-2xl font-serif font-semibold text-slate-900 mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem("hc_admin_token");
    if (!t) { nav("/admin/login"); return; }
    setUser(JSON.parse(localStorage.getItem("hc_admin_user") || "{}"));
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("hc_admin_token");
    localStorage.removeItem("hc_admin_user");
    nav("/admin/login");
  };

  if (!stats) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading dashboard…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/admin/dashboard" className="inline-block">
            <Logo variant="full" tone="dark" size={40} />
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-[13px] text-slate-600 hidden md:block">{user?.email}</div>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-[14px] text-slate-700 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Leads" value={stats.leads.total} accent="blue" />
          <StatCard icon={Mail} label="New Leads" value={stats.leads.new} accent="amber" />
          <StatCard icon={Stethoscope} label="Eligibility Submissions" value={stats.leads.eligibility} accent="teal" />
          <StatCard icon={HeartHandshake} label="CSR Enquiries" value={stats.csr_enquiries} accent="amber" />
          <StatCard icon={GraduationCap} label="Patient Stories" value={stats.patient_stories.total} accent="slate" />
          <StatCard icon={BookOpen} label="Articles" value={stats.articles} accent="slate" />
          <StatCard icon={Newspaper} label="News" value={stats.news} accent="slate" />
          <StatCard icon={Mail} label="Newsletter Subscribers" value={stats.newsletter_subscribers} accent="blue" />
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <Link to="/admin/leads" data-testid="admin-leads-link" className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0369A1] hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="font-serif text-xl font-semibold text-slate-900">Manage Leads (CRM)</div>
              <p className="mt-1 text-slate-600 text-[14.5px]">Triaging, assignment, follow-ups and lead lifecycle.</p>
            </div>
            <ArrowRight />
          </Link>
          <Link to="/admin/content" data-testid="admin-content-link" className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0369A1] hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="font-serif text-xl font-semibold text-slate-900">Patient Stories</div>
              <p className="mt-1 text-slate-600 text-[14.5px]">Verify and publish patient stories.</p>
            </div>
            <ArrowRight />
          </Link>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-serif text-lg font-semibold text-slate-900">Top cities by leads</h2>
          <div className="mt-4 space-y-2">
            {stats.top_cities_by_leads.length === 0 && <div className="text-slate-500 text-sm">No data yet</div>}
            {stats.top_cities_by_leads.map((c) => (
              <div key={c.city} className="flex items-center justify-between border-b border-slate-100 pb-2 text-[15px]">
                <span className="text-slate-700">{c.city || "—"}</span>
                <span className="font-semibold text-[#0369A1]">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
